import {
  StructuredDataSchema,
  StructuredDataSchemaItem,
  StructuredDataStatus,
} from '@repo/shared/dtos/structured-data'
import { Button } from '@repo/ui/button'
import { useT } from '@repo/ui/hooks'
import { InlineCode } from '@repo/ui/inlineCode'
import { Input } from '@repo/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui/table'
import { TooltipWrapper } from '@repo/ui/tooltip'
import { onInput } from '@repo/ui/utils'
import { PenLineIcon, SearchIcon, Trash2Icon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { useAbility } from '@/ability/ability-context.ts'
import { ProcessingButton } from '@/components/admin/structure-data/ProcessingStatus'
import {
  booleanNullToString,
  dateNullToString,
  numberNullToString,
} from '@/components/admin/structure-data/schema/forms/validation'
import { ItemCreator } from '@/components/admin/structure-data/schema/ItemCreator'
import { ItemEditor } from '@/components/admin/structure-data/schema/ItemEditor'
import { adminStoreActions } from '@/store/actions'
import { useStoreDispatch } from '@/store/hooks'
import { selectStructuredDataState } from '@/store/structured-data-state/selectors.ts'

export function Schema({ initialSchema }: { initialSchema: StructuredDataSchema }) {
  const dispatch = useStoreDispatch()

  const { tc, t } = useT({ keyPrefix: 'admin.structuredData' })
  const [searchLabel, setSearchLabel] = useState('')
  const [itemToEdit, setItemToEdit] = useState<StructuredDataSchemaItem | null>(null)

  const filteredSchema = initialSchema.filter((item) =>
    item.label.toLocaleLowerCase().includes(searchLabel.toLocaleLowerCase()),
  )
  const handleEdit = (item: StructuredDataSchemaItem) => {
    setItemToEdit(item)
  }

  const handleUpdate = (updatedItem: StructuredDataSchemaItem) => {
    const updatedSchema = initialSchema.map((item) =>
      item.id === updatedItem.id ? updatedItem : item,
    )
    // TODO: New id for updated item
    void dispatch(adminStoreActions.organization.updateStructuredDataSchema({ updatedSchema }))
  }

  const handleDelete = (item: StructuredDataSchemaItem) => {
    const updatedSchema = initialSchema.filter((i) => i !== item)
    void dispatch(adminStoreActions.organization.updateStructuredDataSchema({ updatedSchema }))
  }

  const handleCreate = (newItem: StructuredDataSchemaItem) => {
    // Ensure default value for all items
    const updatedSchema = [...initialSchema, newItem].map((item) =>
      item.defaultValue === undefined
        ? { ...(item as StructuredDataSchemaItem), defaultValue: null }
        : item,
    )
    void dispatch(adminStoreActions.organization.updateStructuredDataSchema({ updatedSchema }))
  }

  return (
    <>
      <div className="my-4 flex items-center justify-between gap-4">
        <SearchInput value={searchLabel} onChange={setSearchLabel} />
        <div className="flex gap-2 items-center">
          <ItemCreator onCreate={handleCreate} />
          <RefreshButton />
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{tc('label', { cfl: true })}</TableHead>
            <TableHead>{tc('type', { cfl: true })}</TableHead>
            <TableHead>{tc('instructionsToFillValue', { cfl: true })}</TableHead>
            <TableHead>{tc('defaultValue', { cfl: true })}</TableHead>
            <TableHead>{tc('actions', { cfl: true })}</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {filteredSchema.map((item, j) => (
            <TableRow key={j}>
              <TableCell>{item.label}</TableCell>
              <TableCell>
                <InlineCode>{t(`types.${item.type}`)}</InlineCode>
              </TableCell>
              <TableCell>
                <span>{item.instructionsToFillValue}</span>
                {item.type === 'enum' ||
                  (item.type === 'multiselect' && (
                    <div className="mt-1 italic">{item.enumValues.join(', ')}</div>
                  ))}
              </TableCell>
              <TableCell>
                <DisplayDefaultValue value={item.defaultValue} type={item.type} />
              </TableCell>
              <TableCell>
                <div className="flex gap-1">
                  <TooltipWrapper content={tc('delete', { cfl: true })}>
                    <Button variant="outline" size="icon" onClick={() => handleEdit(item)}>
                      <PenLineIcon className="size-4" />
                    </Button>
                  </TooltipWrapper>

                  <TooltipWrapper content={tc('delete', { cfl: true })}>
                    <Button
                      variant="outline"
                      isSensitive
                      size="icon"
                      onClick={() => handleDelete(item)}
                    >
                      <Trash2Icon className="size-4" />
                    </Button>
                  </TooltipWrapper>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {itemToEdit && (
        <ItemEditor item={itemToEdit} onClose={() => setItemToEdit(null)} onUpdate={handleUpdate} />
      )}
    </>
  )
}

function SearchInput({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const { tc } = useT()
  const inputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    inputRef.current?.focus()
  }, [value])
  return (
    <div className="relative">
      <SearchIcon className="absolute left-2 top-1/2 size-4 -translate-y-1/2 opacity-50" />
      <Input
        ref={inputRef}
        value={value}
        onChange={onInput(onChange)}
        placeholder={tc('search', { cfl: true }) + '...'}
        className="w-80 pl-8"
        type="search"
      />
    </div>
  )
}

function DisplayDefaultValue({
  value,
  type,
}: {
  value: StructuredDataSchemaItem['defaultValue']
  type: StructuredDataSchemaItem['type']
}) {
  const { tc } = useT()
  if (value === null || value === undefined || value === '') {
    return <span className="opacity-50">{tc('unknown')}</span>
  }
  if (type === 'boolean' && typeof value === 'boolean') {
    return <span>{tc(booleanNullToString(value))}</span>
  }
  if (type === 'number' && typeof value === 'number') {
    return <span>{tc(numberNullToString(value))}</span>
  }
  if (type === 'date' && typeof value === 'number') {
    return <span>{tc(dateNullToString(value))}</span>
  }
  if (type === 'enum' && typeof value === 'string') {
    return <InlineCode>{value}</InlineCode>
  }
  if (type === 'multiselect' && Array.isArray(value)) {
    return (
      <div className="space-x-2">
        {value.map((v, i) => (
          <InlineCode key={i}>{v}</InlineCode>
        ))}
      </div>
    )
  }
  if (type === 'string' && typeof value === 'string') {
    return <span>{value}</span>
  }
}

function RefreshButton() {
  const { t } = useT({ keyPrefix: 'admin.structuredData' })
  const state = useSelector(selectStructuredDataState)
  const ability = useAbility()
  if (ability.cannot('manage', 'RefreshAllStructuredData')) return null
  return (
    <ProcessingButton
      label={t('refreshAllBeneficiaries')}
      disabled={state?.currentStatus === StructuredDataStatus.PROCESSING}
    />
  )
}
